import { useEffect, useMemo, useState } from "react";
import {
  browserProbeEnvironment,
  browserStartupEnvironment,
  collectStartupDiagnostics,
  createStallReporter,
  createStartupWatchdog,
  probeStartupReachability,
  reportStartupProbe,
  reportStartupStall,
  type StartupStep,
} from "@/utils/startup-watchdog";

const reportStallOnce = createStallReporter((pendingStep) => {
  reportStartupStall(
    collectStartupDiagnostics(pendingStep, browserStartupEnvironment()),
  );
  void probeStartupReachability(browserProbeEnvironment()).then((results) =>
    reportStartupProbe(pendingStep, results),
  );
});

export function useStartupWatchdog(isLoading: boolean): {
  isStalled: boolean;
  markStep: (step: StartupStep) => void;
} {
  const [isStalled, setIsStalled] = useState(false);

  const watchdog = useMemo(
    () =>
      createStartupWatchdog({
        onStall: (pendingStep) => {
          try {
            reportStallOnce(pendingStep);
          } finally {
            setIsStalled(true);
          }
        },
      }),
    [],
  );

  useEffect(() => {
    if (!isLoading) {
      watchdog.complete();
      return;
    }

    watchdog.arm();

    return () => {
      watchdog.disarm();
      setIsStalled(false);
    };
  }, [isLoading, watchdog]);

  return { isStalled: isLoading && isStalled, markStep: watchdog.step };
}
